import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Banknote, CheckCircle2, Pencil, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bank list — kept in sync with backend `_PAYOUT_ALLOWED_BANKS` in server.py.
// Syariah variants are listed explicitly where they exist as distinct entities
// (different bank codes and account number ranges). "Other" lets sellers
// specify a smaller bank we haven't enumerated.
const BANK_OPTIONS = [
  'BCA', 'BCA Syariah',
  'BSI (Bank Syariah Indonesia)',
  'Mandiri', 'BNI', 'BRI',
  'CIMB Niaga', 'CIMB Niaga Syariah',
  'BTPN', 'BTPN Syariah',
  'Permata', 'Danamon', 'OCBC NISP',
  'Maybank Indonesia', 'Bank Jago', 'Panin', 'Mega',
  'Other',
];

/**
 * PayoutBankPanel — embedded in the seller's order card for delivered/completed
 * orders. Shows a saved bank info chip + Change CTA if the seller has saved
 * details previously; otherwise shows the inline input form. Single bank
 * account per user (MVP scope).
 *
 * Variants:
 *  - "review" → shown during the buyer's 48-hour review window (status=shipped + delivered_at)
 *  - "completed" → shown after auto-complete, indicates payout in progress
 */
export default function PayoutBankPanel({ variant = 'review', orderId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [bank, setBank] = useState(null); // {bank_name, bank_name_other, account_holder, account_number}
  const [form, setForm] = useState({ bank_name: '', bank_name_other: '', account_holder: '', account_number: '' });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/payout-bank/me`);
        if (!alive) return;
        if (data && data.bank_name) {
          setBank(data);
          setForm({
            bank_name: data.bank_name,
            bank_name_other: data.bank_name_other || '',
            account_holder: data.account_holder,
            account_number: data.account_number,
          });
          setEditing(false);
        } else {
          setBank(null);
          setEditing(true);
        }
      } catch (err) {
        // 401 / network — show form anyway, save will surface the real error
        setEditing(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    // Sibling-panel sync: saving on one PayoutBankPanel emits this event so any
    // other panels rendered on the same page (multiple orders in My Sales)
    // refresh their cached state without requiring a page reload.
    const onSync = () => { setLoading(true); load(); };
    window.addEventListener('payout-bank-updated', onSync);
    return () => { alive = false; window.removeEventListener('payout-bank-updated', onSync); };
  }, []);

  const handleSave = async () => {
    const payload = {
      bank_name: form.bank_name.trim(),
      bank_name_other: form.bank_name === 'Other' ? form.bank_name_other.trim() : '',
      account_holder: form.account_holder.trim(),
      account_number: form.account_number.replace(/\s+/g, ''),
    };
    if (!payload.bank_name) { toast.error('Please pick your bank from the list.'); return; }
    if (payload.bank_name === 'Other' && !payload.bank_name_other) {
      toast.error('Please type the bank name.'); return;
    }
    if (!payload.account_holder) { toast.error('Please enter the account holder name.'); return; }
    if (!payload.account_number) { toast.error('Please enter the account number.'); return; }

    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/payout-bank/me`, payload);
      setBank(data);
      setEditing(false);
      toast.success('Bank details saved. Admin will process payout once the review window closes.');
      // Broadcast to sibling PayoutBankPanel instances on the same page.
      window.dispatchEvent(new CustomEvent('payout-bank-updated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save bank details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 my-3 flex items-center gap-2 text-sm font-body text-emerald-900">
        <Loader2 size={14} className="animate-spin" /> Loading payout details...
      </div>
    );
  }

  const reviewHeadline = variant === 'review'
    ? 'Item delivered! Buyer has 48 hours to report an issue.'
    : 'Thank you. Your bank details have been saved.';
  const reviewSubcopy = variant === 'review'
    ? 'In the meantime, add your bank account details so admin can process the payout once the review window closes.'
    : '';

  // ── Saved-bank read-only chip ──
  if (bank && !editing) {
    const displayBank = bank.bank_name === 'Other' && bank.bank_name_other ? bank.bank_name_other : bank.bank_name;
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 my-3" data-testid={`payout-bank-saved-${orderId}`}>
        <div className="flex items-start gap-2 mb-2">
          <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-body font-semibold text-emerald-900 leading-snug">{reviewHeadline}</p>
            {reviewSubcopy && (
              <p className="text-xs font-body text-emerald-800 leading-snug mt-0.5">{reviewSubcopy}</p>
            )}
          </div>
        </div>
        <div className="bg-white/60 rounded-xl px-3 py-2 mt-2 flex items-start gap-3">
          <Banknote size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-body text-emerald-700 uppercase tracking-wide font-semibold">Payout to</p>
            <p className="text-sm font-body font-semibold text-emerald-900 leading-snug" data-testid="payout-bank-name">{displayBank}</p>
            <p className="text-xs font-body text-emerald-900 leading-snug" data-testid="payout-account-holder">A.n. {bank.account_holder}</p>
            <p className="text-xs font-body text-emerald-900 leading-snug tabular-nums" data-testid="payout-account-number">{bank.account_number}</p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs font-body text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 underline-offset-2 hover:underline flex-shrink-0"
            data-testid="payout-bank-change-btn"
          >
            <Pencil size={11} /> Change
          </button>
        </div>
      </div>
    );
  }

  // ── Inline edit form ──
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 my-3 space-y-3" data-testid={`payout-bank-form-${orderId}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-body font-semibold text-amber-900 leading-snug">{reviewHeadline}</p>
          <p className="text-xs font-body text-amber-800 leading-snug mt-0.5">{reviewSubcopy}</p>
        </div>
      </div>
      <div>
        <label className="block text-xs font-body font-medium text-amber-900 mb-1">Bank *</label>
        <select
          value={form.bank_name}
          onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
          className="w-full bg-white border border-amber-200 rounded-xl px-3 py-2 font-body text-sm text-[#2C2A29]"
          data-testid="payout-bank-select"
        >
          <option value="">— Choose your bank —</option>
          {BANK_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <p className="text-[11px] font-body text-amber-700 mt-1 leading-snug">
          If your bank has a Syariah variant, please select it explicitly — account numbers differ between regular and Syariah accounts.
        </p>
      </div>
      {form.bank_name === 'Other' && (
        <div>
          <label className="block text-xs font-body font-medium text-amber-900 mb-1">Bank name *</label>
          <input
            type="text"
            value={form.bank_name_other}
            onChange={e => setForm(f => ({ ...f, bank_name_other: e.target.value }))}
            placeholder="Type your bank's full name"
            className="w-full bg-white border border-amber-200 rounded-xl px-3 py-2 font-body text-sm text-[#2C2A29]"
            data-testid="payout-bank-other-input"
          />
        </div>
      )}
      <div>
        <label className="block text-xs font-body font-medium text-amber-900 mb-1">Account holder name *</label>
        <input
          type="text"
          value={form.account_holder}
          onChange={e => setForm(f => ({ ...f, account_holder: e.target.value }))}
          placeholder="Type exactly as printed on your bank account"
          className="w-full bg-white border border-amber-200 rounded-xl px-3 py-2 font-body text-sm text-[#2C2A29]"
          data-testid="payout-account-holder-input"
        />
        <p className="text-[11px] font-body text-amber-700 mt-1 leading-snug">
          Must match your KTP/ID. Payouts to mismatched names will be rejected.
        </p>
      </div>
      <div>
        <label className="block text-xs font-body font-medium text-amber-900 mb-1">Account number *</label>
        <input
          type="text"
          inputMode="numeric"
          value={form.account_number}
          onChange={e => setForm(f => ({ ...f, account_number: e.target.value.replace(/[^0-9 ]/g, '') }))}
          placeholder="Digits only"
          className="w-full bg-white border border-amber-200 rounded-xl px-3 py-2 font-body text-sm text-[#2C2A29] tabular-nums"
          data-testid="payout-account-number-input"
        />
      </div>
      <div className="flex gap-2">
        {bank && (
          <button
            type="button"
            onClick={() => { setEditing(false); setForm({ bank_name: bank.bank_name, bank_name_other: bank.bank_name_other || '', account_holder: bank.account_holder, account_number: bank.account_number }); }}
            className="px-4 py-2 rounded-full border border-amber-200 bg-transparent text-amber-900 font-body text-sm font-semibold hover:bg-amber-100"
            data-testid="payout-bank-cancel-btn"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-emerald-600 text-white rounded-full px-4 py-2 font-body text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="payout-bank-save-btn"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : 'Save bank details'}
        </button>
      </div>
    </div>
  );
}
